import React, { useState, useEffect } from 'react';
import { Wallet, Download, Smartphone, Check } from 'lucide-react';
import { getBrandName } from '../utils/brand';

interface PWAInstallerProps {
  onInstallComplete: () => void;
}

export function PWAInstaller({ onInstallComplete }: PWAInstallerProps) {
  const [installProgress, setInstallProgress] = useState(0);
  const [isInstalling, setIsInstalling] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const isDev = import.meta.env.DEV;

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => {
        registrations.forEach((registration) => 
          registration.unregister().catch(() => {})
        );
      })
      .catch(() => {});
  }, []);

  const installSteps = [
    'Checking device compatibility...',
    'Downloading app bundle...',
    'Installing service worker...',
    'Configuring offline features...',
    'Setting up app shortcuts...',
    'Finalizing installation...'
  ];

  const handleInstall = () => {
    setIsInstalling(true);

    // Service worker registration intentionally disabled to avoid stale caches
    // if ('serviceWorker' in navigator && !isDev) {
    //   navigator.serviceWorker.register('/sw.js').catch(() => {
    //     // Service worker registration failed, continue with mock installation
    //   });
    // }

    let stepIndex = 0;
    const interval = setInterval(() => {
      setInstallProgress((prev) => {
        const newProgress = prev + 16;

        const currentStepIndex = Math.floor(newProgress / 17);
        if (currentStepIndex < installSteps.length && currentStepIndex !== stepIndex) {
          stepIndex = currentStepIndex;
          setCurrentStep(installSteps[stepIndex]);
        }

        if (newProgress >= 100) {
          clearInterval(interval);
          setCurrentStep('Installation complete!');

          setTimeout(() => {
            onInstallComplete();
          }, 1000);
          return 100;
        }
        return newProgress;
      });
    }, 200);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="text-center max-w-sm w-full">
        <div className="relative mb-12">
          <div className="w-28 h-28 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto shadow-2xl">
            <Wallet className="w-14 h-14 text-white" />
          </div>
          <div className="absolute inset-0 w-28 h-28 mx-auto">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-indigo-500 to-purple-600 opacity-20 animate-pulse scale-110"></div>
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-indigo-500 to-purple-600 opacity-10 animate-ping scale-125"></div>
          </div>
        </div>

        <h1 className="text-5xl font-bold text-white mb-3 tracking-tight">{getBrandName()}</h1>
        <p className="text-slate-300 text-xl mb-12 font-medium">Next-Gen Digital Wallet</p>

        <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-xl">
          <Wallet className="w-10 h-10 text-white" />
        </div>

        {!isInstalling ? (
          <div className="space-y-6">
            <button
              onClick={handleInstall}
              className="w-full bg-white text-slate-900 hover:bg-gray-100 font-bold text-lg py-5 rounded-2xl shadow-xl transition-all duration-300 flex items-center justify-center gap-3"
            >
              <Smartphone className="w-6 h-6" />
              Install to Home Screen
            </button>

            <div className="text-center space-y-2">
              <p className="text-slate-300 text-sm">📱 Access like a native app</p>
              <p className="text-slate-300 text-sm">⚡ Instant loading</p>
              <p className="text-slate-300 text-sm">🔒 Secure & private</p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-white bg-opacity-10 rounded-2xl p-6 backdrop-blur-sm border border-white border-opacity-20">
              <div className="flex items-center justify-between mb-3">
                <span className="text-white font-semibold">
                  {installProgress === 100 ? 'Complete!' : 'Installing...'}
                </span>
                <span className="text-white font-bold">{installProgress}%</span>
              </div>
              <div className="w-full bg-white bg-opacity-20 rounded-full h-3 mb-3">
                <div
                  className="bg-gradient-to-r from-indigo-500 to-purple-600 h-3 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${installProgress}%` }}
                ></div>
              </div>
              {currentStep && (
                <p className="text-white text-sm opacity-80">{currentStep}</p>
              )}
            </div>
            {installProgress === 100 && (
              <div className="flex items-center justify-center space-x-2 text-green-300 animate-pulse">
                <Check className="w-5 h-5" />
                <span className="text-sm">App added to home screen!</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}